// Retell AI Voice Provider Implementation

import { RetellWebClient } from 'retell-client-js-sdk'
import { BaseVoiceProvider } from '#app/utils/voice-providers/base-provider.ts'
import {
	type ProviderConfig,
	type TranscriptSegment,
	VoiceProviderError,
	ProviderPhases,
} from '#app/utils/voice-providers/types.ts'

interface RetellTranscriptUpdate {
	transcript: Array<{
		speaker: 'agent' | 'user'
		content: string
		timestamp?: number
	}>
	is_final?: boolean
}

export class RetellProvider extends BaseVoiceProvider {
	private retellClient: RetellWebClient | null = null
	private accessToken: string | null = null
	private lastTranscriptLength = 0

	constructor() {
		super('Retell AI', 'retell')
	}

	async isSupported(): Promise<boolean> {
		// Check if browser supports necessary APIs
		if (typeof window === 'undefined') return false

		// Check for WebRTC support
		const hasWebRTC = !!(
			window.RTCPeerConnection ||
			'webkitRTCPeerConnection' in window ||
			'mozRTCPeerConnection' in window
		)

		// Check for getUserMedia support
		const hasGetUserMedia = !!(
			navigator.mediaDevices?.getUserMedia ||
			'getUserMedia' in navigator ||
			'webkitGetUserMedia' in navigator ||
			'mozGetUserMedia' in navigator
		)

		return hasWebRTC && hasGetUserMedia
	}

	async connect(config?: ProviderConfig): Promise<string> {
		try {
			// Check if already connected or connecting
			if (
				this.connectionState.isConnected ||
				this.connectionState.isConnecting
			) {
				throw new VoiceProviderError(
					'Already connected or connecting',
					this.providerId,
					'CONNECTION_IN_PROGRESS',
				)
			}

			this.config = config || {}
			this.updateConnectionState({
				isConnecting: true,
				currentPhase: ProviderPhases.CONNECTING,
			})

			// Reset state
			this.reset()
			this.updateConnectionState({
				isConnecting: true,
				currentPhase: ProviderPhases.CONNECTING,
			})

			// Get access token from server
			const agentId =
				(typeof config?.agentId === 'string' && config.agentId) ||
				globalThis.ENV?.RETELL_AGENT_ID

			if (!agentId) {
				throw new VoiceProviderError(
					'Retell Agent ID not configured',
					this.providerId,
					'MISSING_AGENT_ID',
				)
			}

			// Fetch access token from server endpoint
			const tokenResponse = await fetch('/api/retell-token', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ agentId }),
			})

			if (!tokenResponse.ok) {
				const error = await tokenResponse.text()
				throw new VoiceProviderError(
					`Failed to get Retell access token: ${error}`,
					this.providerId,
					'TOKEN_FETCH_FAILED',
				)
			}

			const tokenJson: unknown = await tokenResponse.json()
			const accessToken =
				isObject(tokenJson) && 'accessToken' in tokenJson
					? String(tokenJson['accessToken'] ?? '')
					: ''
			if (!accessToken) {
				throw new VoiceProviderError(
					'Invalid Retell token payload',
					this.providerId,
					'TOKEN_PARSE_FAILED',
				)
			}
			this.accessToken = accessToken

			// Initialize Retell client
			this.retellClient = new RetellWebClient()

			// Set up event listeners
			this.setupRetellListeners()

			// Start the call with timeout
			const callPromise = new Promise<string>((resolve, reject) => {
				const timeout = setTimeout(() => {
					reject(
						new VoiceProviderError(
							'Connection timeout - Retell did not respond',
							this.providerId,
							'CONNECTION_TIMEOUT',
						),
					)
				}, 10000)

				// Set up one-time connection listener
				const handleCallStarted = () => {
					clearTimeout(timeout)
					const conversationId = `retell_${Date.now()}`
					this.handleConnect(conversationId)
					resolve(conversationId)
				}

				this.retellClient?.on('call_started', handleCallStarted)

				// Start the call
				if (!this.accessToken) {
					throw new VoiceProviderError(
						'Missing access token',
						this.providerId,
						'MISSING_ACCESS_TOKEN',
					)
				}

				this.retellClient
					?.startCall({
						accessToken: this.accessToken,
						sampleRate:
							typeof config?.sampleRate === 'number'
								? config.sampleRate
								: 24000,
						captureDeviceId:
							typeof config?.captureDeviceId === 'string'
								? config.captureDeviceId
								: 'default',
						emitRawAudioSamples: false,
					})
					.catch((err) => {
						clearTimeout(timeout)
						reject(
							new VoiceProviderError(
								'Failed to start Retell call',
								this.providerId,
								'CALL_START_FAILED',
								err,
							),
						)
					})
			})

			const conversationId = await callPromise
			return conversationId
		} catch (error) {
			this.handleError(
				error instanceof VoiceProviderError
					? error
					: new VoiceProviderError(
							'Failed to connect to Retell',
							this.providerId,
							'CONNECTION_FAILED',
							error,
						),
			)
			throw error
		}
	}

	async disconnect(): Promise<void> {
		if (this.retellClient) {
			this.retellClient.stopCall()
			this.retellClient = null
		}
		this.accessToken = null
		this.handleDisconnect()
	}

	mute(): void {
		// Retell SDK doesn't have direct mute/unmute methods
		// We'll need to handle this through audio stream manipulation
		// For now, we'll just update state
		this.updateConnectionState({ isMuted: true })

		// You could implement actual muting by:
		// 1. Getting the audio stream
		// 2. Disabling the audio track
		// This would require extending the Retell SDK or handling it at a lower level
	}

	unmute(): void {
		this.updateConnectionState({ isMuted: false })
	}

	// Private handler methods
	private setupRetellListeners(): void {
		if (!this.retellClient) return

		// Call lifecycle events
		this.retellClient.on('call_started', () => {
			// Already handled in connect method for initial connection
		})

		this.retellClient.on('call_ended', () => {
			this.handleDisconnect()
		})

		this.retellClient.on('error', (error: unknown) => {
			function hasMessage(v: unknown): v is { message?: unknown } {
				return typeof v === 'object' && v !== null && 'message' in v
			}
			const msg = hasMessage(error) ? String(error.message ?? '') : ''
			this.handleError(
				new VoiceProviderError(
					msg || 'Retell error occurred',
					this.providerId,
					'RETELL_ERROR',
					error,
				),
			)
		})

		// Transcript updates
		this.retellClient.on('update', (update: RetellTranscriptUpdate) => {
			this.handleTranscriptUpdate(update)
		})

		// Agent state events
		this.retellClient.on('agent_start_talking', () => {
			// Agent started speaking
			this.updateConnectionState({ isRecording: false })
		})

		this.retellClient.on('agent_stop_talking', () => {
			// Agent stopped speaking, user can talk now
			this.updateConnectionState({ isRecording: true })
		})
	}

	private handleConnect(conversationId: string): void {
		this.updateConnectionState({
			isConnected: true,
			isConnecting: false,
			isRecording: true,
			conversationId,
			currentPhase: ProviderPhases.GREETING,
			progress: 0,
		})
		this.lastTranscriptLength = 0
	}

	private handleDisconnect(): void {
		this.updateConnectionState({
			isConnected: false,
			isConnecting: false,
			isRecording: false,
			conversationId: null,
			currentPhase: ProviderPhases.IDLE,
			progress: 100,
		})
		this.lastTranscriptLength = 0
	}

	private handleTranscriptUpdate(update: RetellTranscriptUpdate): void {
		if (!update.transcript || update.transcript.length === 0) return

		// Process new transcript segments
		const newSegments = update.transcript.slice(this.lastTranscriptLength)

		for (const segment of newSegments) {
			const transcriptSegment: TranscriptSegment = {
				speaker: segment.speaker === 'agent' ? 'agent' : 'caller',
				text: segment.content,
				timestamp: segment.timestamp || Date.now(),
			}

			this.addTranscriptSegment(transcriptSegment)

			// Extract data from user responses
			if (segment.speaker === 'user') {
				this.extractDataFromTranscript(segment.content)
			} else {
				// Update phase based on agent questions
				this.updatePhaseFromAgentTranscript(segment.content)
			}
		}

		this.lastTranscriptLength = update.transcript.length
	}

	private extractDataFromTranscript(text: string): void {
		const extractedData: Record<string, unknown> = {}

		// Reporter information
		const nameMatch = text.match(
			/(?:my name is|i'm|this is)\s+(?:dr\.?\s+)?([a-z\s]+)/i,
		)
		if (nameMatch?.[1]) {
			extractedData.reporterName = nameMatch[1].trim()
		}

		// Patient demographics
		const ageMatch = text.match(/(\d{1,3})\s*(?:years?\s*old|yr|y\.?o\.?)/i)
		if (ageMatch?.[1]) {
			extractedData.patientAge = ageMatch[1]
		}

		if (/\b(?:female|woman|girl|she|her)\b/i.test(text.toLowerCase())) {
			extractedData.patientSex = 'female'
		} else if (/\b(?:male|man|boy|he|his|him)\b/i.test(text.toLowerCase())) {
			extractedData.patientSex = 'male'
		}

		// Weight extraction
		const weightMatch = text.match(
			/\b(\d{2,3})\s*(?:lbs?|pounds?|kg|kilograms?)\b/i,
		)
		if (weightMatch?.[1]) {
			let weight = parseInt(weightMatch[1])
			if (/lbs?|pounds?/i.test(weightMatch[0])) {
				weight = Math.round(weight * 0.453592)
			}
			extractedData.patientWeight = weight.toString()
		}

		// Drug information
		const drugMatch = text.match(
			/(?:taking|prescribed|medication|drug)\s+([A-Z][a-z]{3,})\b/i,
		)
		if (drugMatch?.[1]) {
			extractedData.productName = drugMatch[1]
		}

		// Dose extraction
		const doseMatch = text.match(
			/\b(\d+\s*(?:mg|mcg|g|ml|units?)\s*(?:once|twice|three times|four times)?\s*(?:daily|per day|a day)?)\b/i,
		)
		if (doseMatch?.[1]) {
			extractedData.dose = doseMatch[1]
		}

		// Event description
		const eventPatterns = [
			/(?:developed|experienced|had|got)\s+(.+?)(?:\.|after|when|$)/i,
			/(?:reaction|side effect|problem|symptom).*?:\s*(.+?)(?:\.|$)/i,
		]
		for (const pattern of eventPatterns) {
			const match = text.match(pattern)
			if (match?.[1] && match[1].length > 10) {
				extractedData.eventDescription = match[1].trim()
				break
			}
		}

		// Notify listeners if data was extracted
		if (Object.keys(extractedData).length > 0) {
			this.eventListeners.onDataExtracted?.(extractedData)
		}
	}

	private updatePhaseFromAgentTranscript(text: string): void {
		const phaseIndicators: Record<string, RegExp> = {
			[ProviderPhases.COLLECTING_REPORTER_INFO]:
				/(?:name|contact|healthcare professional|calling about)/i,
			[ProviderPhases.COLLECTING_PATIENT_INFO]:
				/(?:patient.*age|how old|weight|medical history)/i,
			[ProviderPhases.COLLECTING_EVENT_DETAILS]:
				/(?:what happened|describe.*event|symptoms|when.*start)/i,
			[ProviderPhases.COLLECTING_DRUG_INFO]:
				/(?:medication|drug|taking|prescribed|dose)/i,
			[ProviderPhases.REVIEWING_DETAILS]:
				/(?:summarize|confirm|correct|anything else)/i,
			[ProviderPhases.COMPLETED]:
				/(?:thank you|case.*number|report.*complete)/i,
		}

		for (const [phase, pattern] of Object.entries(phaseIndicators)) {
			if (pattern.test(text)) {
				const progress = this.calculateProgress(phase)
				this.updateConnectionState({
					currentPhase: phase,
					progress,
				})
				break
			}
		}
	}

	private calculateProgress(phase: string): number {
		const phaseOrder = [
			ProviderPhases.GREETING,
			ProviderPhases.COLLECTING_REPORTER_INFO,
			ProviderPhases.COLLECTING_PATIENT_INFO,
			ProviderPhases.COLLECTING_EVENT_DETAILS,
			ProviderPhases.COLLECTING_DRUG_INFO,
			ProviderPhases.REVIEWING_DETAILS,
			ProviderPhases.COMPLETED,
		]

		const index = phaseOrder.indexOf(phase)
		if (index === -1) return 0
		return Math.round((index / (phaseOrder.length - 1)) * 100)
	}
}
function isObject(val: unknown): val is Record<string, unknown> {
	return typeof val === 'object' && val !== null
}
